import * as React from 'react';
import { useTranslation } from 'react-i18next';

import type { Order } from '@klareospace/pharos-lib-types';
import {
  Button,
  DateTimeRangePicker,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  ErrorBanner,
} from '@klareospace/pharos-lib-web-utils';

import type { TimeWindowEditTarget } from '../hooks/use-time-window-edit';
import { useTimeWindowEdit } from '../hooks/use-time-window-edit';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface TimeWindowEditDialogProps {
  readonly onClose: () => void;
  readonly open: boolean;
  readonly order: Order;
  readonly target: TimeWindowEditTarget;
}

// ─── Component ────────────────────────────────────────────────────────────────

/** Timewindow pop-in for all three `TimeWindowEditTarget` rows; only the title/summary label differ per target. */
export function TimeWindowEditDialog({ onClose, open, order, target }: TimeWindowEditDialogProps): React.JSX.Element {
  const { t: tCommon } = useTranslation('common');
  const { t } = useTranslation('orders');
  const { errorMessage, isSaveDisabled, onChange, onSubmit, value } = useTimeWindowEdit({
    onSuccess: onClose,
    order,
    target,
  });
  const isRequestedTimeWindow = target === 'requestedTimeWindow';

  function handleOpenChange(nextOpen: boolean): void {
    if (!nextOpen) onClose();
  }

  return (
    <Dialog onOpenChange={handleOpenChange} open={open}>
      <DialogContent className="overflow-hidden rounded-xl p-0 sm:max-w-[560px]">
        <DialogHeader className="px-4 pb-2 pt-4">
          <DialogTitle className="text-base font-semibold">
            {t(
              isRequestedTimeWindow ? 'drawer.edit.timeWindow.requestedTitle' : 'drawer.edit.timeWindow.timeslotTitle',
            )}
          </DialogTitle>
        </DialogHeader>

        {errorMessage !== null && (
          <div className="px-4">
            <ErrorBanner message={errorMessage} />
          </div>
        )}

        <DateTimeRangePicker
          onChange={onChange}
          summaryLabel={t(isRequestedTimeWindow ? 'common.requestedTimewindow' : 'common.timeslot')}
          toLabel={t('drawer.edit.timeWindow.to')}
          value={value}
        />

        <div className="flex justify-end gap-2 border-t px-4 py-3">
          <Button onClick={onClose} size="sm" type="button" variant="outline">
            {tCommon('actions.cancel')}
          </Button>
          <Button disabled={isSaveDisabled} onClick={onSubmit} size="sm" type="button">
            {tCommon('actions.save')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
